// Shared factory for resource list-page URL search-state. Every standardised
// list route (contacts, organisations, ...) owns the same {q, page, perPage,
// sort, direction} shape over TanStack Router; this module is the single home
// for that boilerplate so a new resource is config, not a clone.
//
// Replaces the per-resource parseXSearch / useXRouterSearch hooks.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export type ResourceSortDirection = 'asc' | 'desc' | '';

export interface ResourceListSearchParams<TSort extends string = string> {
  q: string;
  page: number;
  perPage: number;
  sort: TSort | '';
  direction: ResourceSortDirection;
}

const VALID_DIRECTIONS: ReadonlySet<'asc' | 'desc'> = new Set(['asc', 'desc']);

export interface ResourceSearchParserConfig<TSort extends string> {
  defaults: ResourceListSearchParams<TSort>;
  // Sortable column ids, excluding the empty "no sort" value (always allowed).
  validSorts: ReadonlyArray<TSort>;
  // When true, a direction is only kept alongside a valid sort column, so a
  // dangling ?direction= (e.g. ?sort=bogus&direction=desc) can't imply a sort
  // the table isn't applying. Organisations relies on this; Contacts does not.
  requireSortForDirection?: boolean;
}

// Builds a TanStack `validateSearch` handler that coerces unknown URL input
// into the typed params shape.
export function createResourceSearchParser<TSort extends string>(
  config: ResourceSearchParserConfig<TSort>,
): (raw: Record<string, unknown>) => ResourceListSearchParams<TSort> {
  const { defaults, requireSortForDirection = false } = config;
  const validSorts: ReadonlySet<TSort | ''> = new Set<TSort | ''>([...config.validSorts, '']);

  return function parseResourceSearch(raw) {
    const sortValid = validSorts.has(raw.sort as TSort | '');
    const sort = (sortValid ? raw.sort : defaults.sort) as TSort | '';
    // A sort the URL names but we don't know drops its direction too. A
    // missing sort doesn't: stripSearchParams removes a default sort column,
    // leaving its direction behind on its own.
    const sortRejected = raw.sort !== undefined && !sortValid;
    const directionAllowed = requireSortForDirection ? Boolean(sort) && !sortRejected : true;
    const direction: ResourceSortDirection =
      directionAllowed && VALID_DIRECTIONS.has(raw.direction as 'asc' | 'desc')
        ? (raw.direction as 'asc' | 'desc')
        : defaults.direction;
    const page = Number(raw.page);
    const perPage = Number(raw.perPage);
    return {
      q: typeof raw.q === 'string' ? raw.q : defaults.q,
      page: Number.isFinite(page) && page > 0 ? page : defaults.page,
      perPage: Number.isFinite(perPage) && perPage > 0 ? perPage : defaults.perPage,
      sort,
      direction,
    };
  };
}

export interface ResourceRouterSearch<TSort extends string> {
  search: ResourceListSearchParams<TSort>;
  navigate: (updates: Partial<ResourceListSearchParams<TSort>>) => void;
  setQuery: (q: string) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSort: (sort: TSort | '', direction: ResourceSortDirection) => void;
}

// Builds the typed useXRouterSearch hook for a route. `routePath` must be a
// registered route id (e.g. '/contacts'). Sort / query changes reset page so
// previous pagination state never bleeds into the new view.
export function createResourceRouterSearch<TSort extends string>(routePath: string) {
  type Params = ResourceListSearchParams<TSort>;
  type NavigateFn = (opts: { search: (prev: Params) => Params; replace?: boolean }) => void;

  return function useResourceRouterSearch(): ResourceRouterSearch<TSort> {
    // `from` is a route-id-constrained literal in TanStack's types; this module
    // is route-agnostic, so we read/navigate with a contained cast and re-assert
    // the known params shape on the way out. Runtime behaviour is unchanged.
    const search = useSearch({ from: routePath } as never) as Params;
    const tanNavigate = useNavigate({ from: routePath } as never) as unknown as NavigateFn;

    const navigate = useCallback(
      (updates: Partial<Params>) => {
        tanNavigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
      },
      [tanNavigate],
    );

    return useMemo(
      () => ({
        search,
        navigate,
        setQuery: (q: string) => navigate({ q, page: 1 }),
        setPage: (page: number) => navigate({ page }),
        setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
        setSort: (sort: TSort | '', direction: ResourceSortDirection) =>
          navigate({ sort, direction, page: 1 }),
      }),
      [search, navigate],
    );
  };
}
